import React from 'react';
import { Subscription } from 'rxjs';
import { lazyInject } from '../../../context/module';
import { BulkZipService } from '../../../services/bulk-zip.service';
import { BulkZipExcludedFile, BulkZipJobView } from '../../../types/bulk-zip';
import { DownloadsCenterRow } from './row.component';
import { IMPERSONATION_FORBIDDEN_MESSAGE, notifyImpersonationForbidden } from '../../../utils/impersonation-storage';
import { isImpersonationForbiddenError, viewingAsUser } from '../../../utils/impersonation-guard';
import styles from './downloads-center.component.module.scss';

interface State {
    open: boolean;
    jobs: BulkZipJobView[];
    coverage: BulkZipExcludedFile[] | null;
    /** Admin v2: "Not allowed while viewing as a user." after a refused download / retry. */
    notice: string | null;
}

export class DownloadsCenter extends React.Component<{}, State> {
    @lazyInject(BulkZipService) private svc: BulkZipService;

    state: State = { open: false, jobs: [], coverage: null, notice: null };

    private subs: Subscription[] = [];

    /**
     * Fallback refresh while a job is still working.
     *
     * Progress reaches this panel over WebSockets, and websockets are switched off in settings
     * (`useWebsockets: false` on QA) -- so the panel sat on "BUILDING 0% (0/3)" while the server
     * had the job COMPLETED 3/3, and only a page reload showed the truth. That was survivable
     * while nobody opened the panel; opening it on submit made it the main thing the user sees.
     *
     * Polls only while the panel is open AND something is unfinished, and stops on its own the
     * moment every job is terminal, so an idle panel costs nothing.
     */
    private pollTimer: any = null;

    componentDidMount() {
        this.subs.push(
            this.svc.jobs$.subscribe((jobs) => this.setState({ jobs })),
        );
        this.subs.push(
            this.svc.events$.subscribe((ev) => {
                if (ev.kind === 'COVERED') {
                    this.setState({ open: true, coverage: ev.coverage });
                } else if (ev.kind === 'CREATED' || ev.kind === 'EXISTING') {
                    // Fetch once straight away: the submit response is a snapshot from before
                    // the builder started, so without this the first thing shown is stale.
                    this.setState({ open: true });
                    this.svc.list().catch(() => { /* the poll will retry */ });
                }
            }),
        );
        this.subs.push(
            this.svc.openRequested$.subscribe(() => this.setState({ open: true })),
        );
    }

    componentDidUpdate() {
        this.syncPolling();
    }

    private hasUnfinishedJob(): boolean {
        return this.state.jobs.some(j => !j.failed && j.processStage !== 'COMPLETED');
    }

    private syncPolling() {
        const wanted = this.state.open && this.hasUnfinishedJob();
        if (wanted && !this.pollTimer) {
            this.pollTimer = setInterval(() => {
                this.svc.list().catch(() => { /* a failed refresh just means we try again */ });
            }, 2000);
        } else if (!wanted && this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private stopPolling() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    componentWillUnmount() {
        this.stopPolling();
        this.subs.forEach((s) => s.unsubscribe());
        this.subs = [];
    }

    close = () => {
        this.stopPolling();
        this.setState({ open: false, coverage: null, notice: null });
    };

    private onDownload = (jobId: string) => {
        // Admin v2: the server refuses ZIP downloads (and their download tokens) while viewing as a user. A
        // navigation would land on the raw 403 JSON, so say it here instead and stay on the page.
        if (viewingAsUser()) {
            this.setState({ notice: IMPERSONATION_FORBIDDEN_MESSAGE });
            notifyImpersonationForbidden();
            return;
        }
        window.location.href = this.svc.downloadUrl(jobId);
    };

    private onDelete = (jobId: string) => {
        this.svc.delete(jobId);
    };

    // Retry resubmits the job's original inputs. The API now returns `fileIds` on the
    // job view, so this no longer needs the user to go back to the downloads page.
    private onRetry = (jobId: string) => {
        const job = this.state.jobs.find(j => j.jobId === jobId);
        if (!job || !job.fileIds || job.fileIds.length === 0) {
            console.warn('[bulk-zip] retry: job has no fileIds', jobId);
            return;
        }
        this.svc.submit(job.fileIds).catch(err => {
            if (isImpersonationForbiddenError(err)) this.setState({ notice: IMPERSONATION_FORBIDDEN_MESSAGE });
            console.error('[bulk-zip] retry failed', err);
        });
    };

    /**
     * The covering ZIPs, one entry each, with how many of the requested files they hold and
     * whether that ZIP can be downloaded right now.
     */
    private coverageByJob(): Array<{ jobId: string; label: string; count: number; ready: boolean }> {
        const byJob = new Map<string, { jobId: string; label: string; count: number; ready: boolean }>();
        for (const c of this.state.coverage || []) {
            const jobId = c.sourceJobId;
            const existing = byJob.get(jobId);
            if (existing) {
                existing.count += 1;
                continue;
            }
            const job = this.state.jobs.find(j => j.jobId === jobId);
            byJob.set(jobId, {
                jobId,
                label: `${c.sourceJobLabel ?? jobId}.zip`,
                count: 1,
                ready: !!job && job.processStage === 'COMPLETED' && !job.failed && job.active,
            });
        }
        return [...byJob.values()];
    }

    render() {
        if (!this.state.open) return null;
        return (
            <div className={styles['downloads-center']} role="dialog" aria-label="Bulk Downloads">
                <div className={styles['header']}>
                    <h3>Bulk Downloads</h3>
                    <button onClick={this.close} aria-label="Close">
                        ×
                    </button>
                </div>
                {this.state.notice && (
                    <p role="alert" className={styles['empty']} data-testid="downloads-center-notice">{this.state.notice}</p>
                )}
                {this.state.coverage && this.state.coverage.length > 0 && (
                    // Grouped by the ZIP they are in, not listed per file. This printed one line
                    // per file, each showing that file's *job* label -- so three files from one
                    // job printed the same name three times, and none of them was clickable.
                    <div className={styles['coverage-notice']}>
                        <p>These files are already in a ZIP you asked for:</p>
                        <ul>
                            {this.coverageByJob().map((g) => (
                                <li key={g.jobId}>
                                    <strong>{g.label}</strong>
                                    {` — ${g.count} ${g.count === 1 ? 'file' : 'files'}`}
                                    {g.ready && (
                                        <button type="button" className={styles['coverage-download']}
                                            onClick={() => this.onDownload(g.jobId)}>
                                            Download
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className={styles['rows']}>
                    {this.state.jobs.map((j) => (
                        <DownloadsCenterRow
                            key={j.jobId}
                            job={j}
                            onDownload={this.onDownload}
                            onDelete={this.onDelete}
                            onRetry={this.onRetry}
                        />
                    ))}
                    {this.state.jobs.length === 0 && (
                        <p className={styles['empty']}>No bulk downloads yet.</p>
                    )}
                </div>
            </div>
        );
    }
}
